import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { WishlistApi } from "services/WishlistApi";
import Helpers from "@utils/Helpers";
import { errorLog } from "logging";

export interface WishlistItem {
  product_id: number;
  name?: string | null;
  price?: number | null;
  image_url?: string | null;
  producer_id?: number | null;
}

interface WishlistContextType {
  items: WishlistItem[];
  add: (productId: number) => Promise<void>;
  remove: (productId: number) => Promise<void>;
  refresh: () => Promise<void>;
  isInWishlist: (productId: number) => boolean;
  isLoading: boolean;
  hasError: boolean;
}

const WishlistContext = createContext<WishlistContextType | undefined>(
  undefined
);

export const WishlistProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setItems([]);
      setHasError(false);
      return;
    }
    setIsLoading(true);
    setHasError(false);
    try {
      const response = await WishlistApi.getItems();
      const payload = response?.data?.items ?? response?.data?.data?.items;
      setItems(Array.isArray(payload) ? payload : []);
    } catch (error) {
      setHasError(true);
      Helpers.notification.error("Can't load your watchlist");
      errorLog("WishlistContext.tsx - refresh", error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const add = async (productId: number) => {
    try {
      await WishlistApi.addItem(productId);
      Helpers.notification.success("Added to watchlist");
      await refresh();
    } catch (error) {
      Helpers.notification.error("Can't add to watchlist");
      errorLog("WishlistContext.tsx - add", error);
    }
  };

  const remove = async (productId: number) => {
    try {
      await WishlistApi.removeItem(productId);
      setItems((current) =>
        current.filter((item) => item.product_id !== productId)
      );
      Helpers.notification.success("Removed from watchlist");
      await refresh();
    } catch (error) {
      Helpers.notification.error("Can't remove from watchlist");
      errorLog("WishlistContext.tsx - remove", error);
    }
  };

  const isInWishlist = (productId: number) =>
    items.some((item) => item.product_id === productId);

  return (
    <WishlistContext.Provider
      value={{ items, add, remove, refresh, isInWishlist, isLoading, hasError }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error("useWishlist must be used within a WishlistProvider");
  }
  return context;
};
